import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { BrowserProvider } from 'ethers';
import { ACTIVE_CHAIN, ACTIVE_CHAIN_ID } from '../config/network';
import { detectInjected, walletName } from '../utils/wallet';
import { useNotify } from './NotificationContext';

const WalletContext = createContext(null);
export const useWallet = () => useContext(WalletContext);

const LS_KEY = 'grx.wallet.connected';

export const WalletProvider = ({ children }) => {
  const notify = useNotify();
  const [provider, setProvider] = useState(null);       // ethers BrowserProvider
  const [rawProvider, setRawProvider] = useState(null); // window.ethereum or wc provider
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [signer, setSigner] = useState(null);
  const [wcInstance, setWcInstance] = useState(null);

  const isRightNetwork = chainId === ACTIVE_CHAIN_ID;

  // -------- Switch / add chain (no page refresh) --------
  const switchNetwork = async (raw) => {
    const p = raw || rawProvider;
    if (!p) return;
    try {
      await p.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: ACTIVE_CHAIN.chainIdHex }],
      });
    } catch (e) {
      if (e && (e.code === 4902 || e.code === -32603 || e?.data?.originalError?.code === 4902)) {
        try {
          await p.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: ACTIVE_CHAIN.chainIdHex,
              chainName: ACTIVE_CHAIN.name,
              nativeCurrency: ACTIVE_CHAIN.nativeCurrency,
              rpcUrls: ACTIVE_CHAIN.rpcUrls,
              blockExplorerUrls: ACTIVE_CHAIN.blockExplorerUrls,
            }],
          });
        } catch (addErr) {
          if (notify) notify.error('Network Error', 'Could not add the required network to your wallet.');
        }
      } else if (e && e.code === 4001) {
        if (notify) notify.error('Rejected', 'Network switch was rejected.');
      } else if (notify) {
        notify.error('Network Error', 'Failed to switch network.');
      }
    }
  };

  // -------- Attach listeners (imperative, not a callback dep) --------
  const attachListeners = (p) => {
    if (!p || !p.on) return;
    // remove any previous
    try { p.removeAllListeners && p.removeAllListeners(); } catch {}

    p.on('accountsChanged', (accs) => {
      if (!accs || accs.length === 0) {
        // disconnect
        setAccount(null);
        setSigner(null);
        setProvider(null);
        try { localStorage.removeItem(LS_KEY); } catch {}
        if (notify) notify.info('Disconnected', 'Wallet disconnected.');
      } else {
        setAccount(accs[0].toLowerCase());
        // refresh signer
        try {
          const bp = new BrowserProvider(p);
          setProvider(bp);
          bp.getSigner().then((s) => setSigner(s)).catch(() => {});
        } catch {}
      }
    });

    p.on('chainChanged', (cidHex) => {
      const cid = parseInt(cidHex, 16);
      setChainId(cid);
      // refresh provider so BrowserProvider knows new chain
      try {
        const bp = new BrowserProvider(p);
        setProvider(bp);
        bp.getSigner().then((s) => setSigner(s)).catch(() => {});
      } catch {}
      if (cid !== ACTIVE_CHAIN_ID) {
        // proactively try to switch back (silent)
        setTimeout(() => switchNetwork(p), 250);
      }
    });

    p.on('disconnect', () => {
      setAccount(null);
      setSigner(null);
      setProvider(null);
      setChainId(null);
      try { localStorage.removeItem(LS_KEY); } catch {}
    });
  };

  // -------- Connect: injected --------
  const connectInjected = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const p = detectInjected();
      if (!p) {
        if (notify) notify.error('No Wallet', 'No wallet detected. Please install or open in Trust Wallet browser.');
        setConnecting(false);
        return;
      }
      const accs = await p.request({ method: 'eth_requestAccounts' });
      const cidHex = await p.request({ method: 'eth_chainId' });
      const cid = parseInt(cidHex, 16);

      setRawProvider(p);
      setAccount((accs[0] || '').toLowerCase());
      setChainId(cid);

      const bp = new BrowserProvider(p);
      setProvider(bp);
      const s = await bp.getSigner();
      setSigner(s);

      attachListeners(p);
      try { localStorage.setItem(LS_KEY, 'injected'); } catch {}

      if (cid !== ACTIVE_CHAIN_ID) {
        await switchNetwork(p);
      }
      if (notify) notify.success('Connected', walletName(p));
    } catch (e) {
      if (e && e.code === 4001) {
        if (notify) notify.error('Rejected', 'Connection request rejected.');
      } else if (notify) notify.error('Connect Failed', e?.message || 'Could not connect.');
    } finally {
      setConnecting(false);
    }
  };

  // -------- Connect: WalletConnect v2 (mobile deep-link) --------
  const connectWalletConnect = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const { EthereumProvider } = await import('@walletconnect/ethereum-provider');
      const projectId = 'YOUR_WALLETCONNECT_PROJECT_ID'; // replace with real projectId
      const wc = await EthereumProvider.init({
        projectId,
        chains: [ACTIVE_CHAIN_ID],
        showQrModal: true,
        rpcMap: { [ACTIVE_CHAIN_ID]: ACTIVE_CHAIN.rpcUrls[0] },
        metadata: {
          name: 'Graphenex',
          description: 'Graphenex Ecosystem',
          url: typeof window !== 'undefined' ? window.location.origin : '',
          icons: [],
        },
      });
      await wc.enable();
      setWcInstance(wc);
      setRawProvider(wc);
      const bp = new BrowserProvider(wc);
      setProvider(bp);
      const s = await bp.getSigner();
      setSigner(s);
      const addr = (await s.getAddress()).toLowerCase();
      setAccount(addr);
      const cid = Number(wc.chainId);
      setChainId(cid);
      attachListeners(wc);
      try { localStorage.setItem(LS_KEY, 'walletconnect'); } catch {}
      if (cid !== ACTIVE_CHAIN_ID) await switchNetwork(wc);
      if (notify) notify.success('Connected', 'WalletConnect');
    } catch (e) {
      if (notify) notify.error('Connect Failed', e?.message || 'WalletConnect failed.');
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      if (wcInstance && wcInstance.disconnect) await wcInstance.disconnect();
    } catch {}
    setAccount(null);
    setSigner(null);
    setProvider(null);
    setRawProvider(null);
    setChainId(null);
    setWcInstance(null);
    try { localStorage.removeItem(LS_KEY); } catch {}
    if (notify) notify.info('Disconnected', 'Wallet disconnected.');
  };

  // -------- Auto-reconnect on load --------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = localStorage.getItem(LS_KEY);
        if (!saved) return;
        if (saved === 'injected') {
          const p = detectInjected();
          if (!p) return;
          const accs = await p.request({ method: 'eth_accounts' });
          if (!accs || accs.length === 0) return;
          const cidHex = await p.request({ method: 'eth_chainId' });
          const cid = parseInt(cidHex, 16);
          if (cancelled) return;
          setRawProvider(p);
          setAccount(accs[0].toLowerCase());
          setChainId(cid);
          const bp = new BrowserProvider(p);
          setProvider(bp);
          const s = await bp.getSigner();
          setSigner(s);
          attachListeners(p);
          if (cid !== ACTIVE_CHAIN_ID) await switchNetwork(p);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    provider,
    signer,
    account,
    chainId,
    connecting,
    isRightNetwork,
    connectInjected,
    connectWalletConnect,
    disconnect,
    switchNetwork: () => switchNetwork(),
    activeChain: ACTIVE_CHAIN,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [provider, signer, account, chainId, connecting, isRightNetwork]);

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};
